import React, { useState, useCallback } from 'react';
import { useSession } from '../contexts/SessionContext';
import './VerificationScreen.css';

/**
 * ID verification screen shown before clients can access portal sections.
 * Clients verify their identity by entering the last 4 digits of their SA ID number.
 * Max 5 attempts before lockout.
 */
export default function VerificationScreen() {
  const {
    verify,
    isVerifying,
    isLockedOut,
    attemptsRemaining,
    error,
    clearError,
  } = useSession();

  const [idDigits, setIdDigits] = useState('');
  const [inputError, setInputError] = useState('');

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setInputError('');

    // Validate input
    const cleaned = idDigits.replace(/\s/g, '');
    if (cleaned.length !== 4) {
      setInputError('Please enter exactly 4 digits.');
      return;
    }
    if (!/^\d{4}$/.test(cleaned)) {
      setInputError('Please enter numbers only.');
      return;
    }

    await verify(cleaned);
  }, [idDigits, verify]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
    setIdDigits(value);
    setInputError('');
    if (error) clearError();
  }, [error, clearError]);

  if (isLockedOut) {
    return (
      <div className="verification-screen" data-testid="verification-screen">
        <div className="verification-card verification-card--locked">
          <div className="verification-icon verification-icon--locked" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h2 className="verification-title">Account Locked</h2>
          <p className="verification-message verification-message--error" data-testid="lockout-message">
            Your access has been locked due to too many failed verification attempts.
            Please contact Dola Money for assistance.
          </p>
          <a
            href="tel:0115682963"
            className="btn btn--primary verification-call-btn"
            data-testid="lockout-call-btn"
          >
            Call Dola Money: 011 568 2963
          </a>
          <p className="verification-lockout-email">
            Or email us at{' '}
            <a href="mailto:customercare@dola.co.za" data-testid="lockout-email-link">customercare@dola.co.za</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-screen" data-testid="verification-screen">
      <div className="verification-card">
        <div className="verification-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 5 5v4"/>
          </svg>
        </div>

        <h2 className="verification-title">Verify Your Identity</h2>
        <p className="verification-subtitle">
          To protect your personal information, please enter the last 4 digits of your SA ID number.
        </p>

        <form onSubmit={handleSubmit} className="verification-form" noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="id-digits">
              Last 4 digits of your ID number
            </label>
            <input
              id="id-digits"
              type="text"
              className={`form-input verification-input ${(inputError || error) ? 'form-input--error' : ''}`}
              value={idDigits}
              onChange={handleInputChange}
              placeholder="_ _ _ _"
              maxLength={4}
              inputMode="numeric"
              pattern="\d{4}"
              autoComplete="off"
              autoFocus
              disabled={isVerifying}
              aria-describedby="id-digits-help"
              data-testid="id-digits-input"
            />
            <p id="id-digits-help" className="form-hint">
              Example: If your ID number is 880512 5088 08 3, enter <strong>0883</strong>
            </p>
          </div>

          {inputError && (
            <p className="form-error verification-error" role="alert" data-testid="input-error">
              {inputError}
            </p>
          )}

          {error && !inputError && (
            <p className="form-error verification-error" role="alert" data-testid="verify-error">
              {error}
            </p>
          )}

          {attemptsRemaining < 5 && attemptsRemaining > 0 && (
            <p className="verification-attempts" role="status" data-testid="attempts-remaining">
              {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
            </p>
          )}

          <button
            type="submit"
            className="btn btn--primary verification-submit-btn"
            disabled={isVerifying || idDigits.length < 4}
            data-testid="verify-btn"
          >
            {isVerifying ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Verifying...
              </>
            ) : (
              'Verify Identity'
            )}
          </button>
        </form>

        <div className="verification-help">
          <p>
            Need help? Call <a href="tel:0115682963">011 568 2963</a> or email <a href="mailto:customercare@dola.co.za">customercare@dola.co.za</a>
          </p>
        </div>
      </div>
    </div>
  );
}
